import { useState, useEffect, useRef, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { Cpu, Plus, Trash2, Wifi, WifiOff, Scan, Settings, Upload, FileUp, Usb, Radio, StopCircle, Play, Square, ChevronDown, ChevronRight, Lock, Download, Save, Loader2, Smartphone, Eye, EyeOff, Palette, Tag, Server } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useUSBConnection } from "@/contexts/USBConnectionContext";
import { UpgradeModal } from "@/components/UpgradeModal";
import { parseScanData } from "@/utils/scanDataParser";
import { BroadcastControls, RemoteSpectrumViewer } from "@/components/hardware/RemoteSpectrumViewer";
import { ResponsiveSystemsView, SystemsToggleButton } from "@/components/hardware/FloatingSystems";
import { RelacartConnectionPanel } from "@/components/hardware/RelacartConnectionPanel";
import { MarkerConfigPopover, MARKER_COLORS, DEFAULT_MARKER_SETTINGS } from "@/components/hardware/MarkerConfigPopover";
import { USBTroubleshooting, MobileDeviceWarning } from "@/components/hardware/USBTroubleshooting";
import RFNetworkDashboard from "@/components/hardware/RFNetworkDashboard";
import CoordinationModePanel from "@/components/rf/CoordinationModePanel";
import { isFeatureAvailable } from "@/lib/featureFlags";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
  Bar,
  ComposedChart,
} from "recharts";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1A1A] border border-white/10 rounded-sm p-3">
        <p className="font-mono text-sm text-[#34C759]">{typeof label === 'number' ? label.toFixed(3) : label} MHz</p>
        <p className="font-mono text-xs text-[#A1A1AA]">
          Level: {payload[0].value.toFixed(1)} dBm
        </p>
      </div>
    );
  }
  return null;
};

// Check if Web Serial API is supported and allowed
const isWebSerialSupported = () => {
  return 'serial' in navigator;
};

const isWebSerialAllowed = async () => {
  if (!isWebSerialSupported()) return false;
  try {
    // Check if we can access the serial API (will fail in iframes with restrictive policies)
    await navigator.serial.getPorts();
    return true;
  } catch (e) {
    return false;
  }
};

export default function HardwarePage({ user, onLogout }) {
  // Global USB Connection from context - for both HID and Serial
  const { 
    hidConnected, 
    hidDevice, 
    hidDeviceInfo,
    hidScanning,
    isHIDSupported,
    connectHID, 
    disconnectHID, 
    startHIDScan, 
    stopHIDScan,
    rawDataLog: globalRawDataLog,
    // Serial (tinySA) - from global context
    serialConnected,
    serialPort,
    serialDeviceInfo,
    serialScanning,
    isSerialSupported,
    tinySAData,
    connectSerial,
    disconnectSerial,
    startSerialScan,
    stopSerialScan,
  } = useUSBConnection();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [scanning, setScanning] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [importData, setImportData] = useState("");
  const [importName, setImportName] = useState("");
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [form, setForm] = useState({
    device_type: "rf_analyzer",
    device_name: "",
    connection_type: "usb",
    ip_address: "",
    port: "5000",
    serial_port: "",
    baud_rate: "500000",
  });

  // Map context state to local variable names for compatibility
  const usbConnected = serialConnected;
  const liveScanning = serialScanning;
  const liveData = tinySAData;
  
  // UI-only local state
  const [serialAllowed, setSerialAllowed] = useState(true);
  const [detectedDevice, setDetectedDevice] = useState("Auto");
  const [rawDataLog, setRawDataLog] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [tinySADataBuffer, setTinySADataBuffer] = useState([]);
  const [savingScan, setSavingScan] = useState(false);
  const [showSaveScanDialog, setShowSaveScanDialog] = useState(false);
  const [scanName, setScanName] = useState("");
  
  // Noise floor level (user adjustable)
  const [noiseFloorLevel, setNoiseFloorLevel] = useState(-85);
  
  // Systems and interference detection
  const [systems, setSystems] = useState([]);
  const [rfAlerts, setRfAlerts] = useState([]);
  const RF_ALERT_THRESHOLD = -75; // dBm - signals above this are considered interference
  
  // Frequency marker configuration
  const [markerConfig, setMarkerConfig] = useState(() => {
    const saved = localStorage.getItem('avx_marker_config');
    return saved ? JSON.parse(saved) : {};
  });
  const [globalMarkerSettings, setGlobalMarkerSettings] = useState(() => {
    const saved = localStorage.getItem('avx_global_marker_settings');
    return saved ? JSON.parse(saved) : DEFAULT_MARKER_SETTINGS;
  });
  const [showMarkerPanel, setShowMarkerPanel] = useState(false);
  
  // Save marker config to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('avx_marker_config', JSON.stringify(markerConfig));
  }, [markerConfig]);
  
  useEffect(() => {
    localStorage.setItem('avx_global_marker_settings', JSON.stringify(globalMarkerSettings));
  }, [globalMarkerSettings]);
  
  // Get marker color for a system
  const getMarkerColor = (system) => {
    const config = markerConfig[system.id];
    if (config?.color) return config.color;
    // Assign default color based on index
    const idx = systems.findIndex(s => s.id === system.id);
    return MARKER_COLORS[idx % MARKER_COLORS.length].value;
  };
  
  // Update marker config for a system
  const updateMarkerConfig = (systemId, updates) => {
    setMarkerConfig(prev => ({
      ...prev,
      [systemId]: { ...prev[systemId], ...updates }
    }));
  };
  
  // Subscription state
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("hardware");
  
  // Remote viewing state (for iPad/mobile)
  const [showRemoteViewer, setShowRemoteViewer] = useState(false);
  
  // Floating/Sidebar systems state
  const [showSystemsView, setShowSystemsView] = useState(true);
  
  const readerRef = useRef(null);
  const writerRef = useRef(null);
  const scanIntervalRef = useRef(null);

  useEffect(() => {
    fetchDevices();
    fetchSystems();
    fetchSubscriptionUsage();
    // Check if serial is allowed
    isWebSerialAllowed().then(allowed => setSerialAllowed(allowed));
    // Note: NO cleanup on unmount - USB connection and scanning 
    // are managed by the global context and should persist
    
    // Log state for debugging persistent scans
    if (serialConnected && serialScanning) {
      console.log("[HardwarePage] Remounted with active scan:", {
        serialConnected,
        serialScanning,
        tinySADataPoints: tinySAData?.length || 0
      });
    }
  }, []);

  const fetchSubscriptionUsage = async () => {
    try {
      const response = await axios.get(`${API_BASE}/subscriptions/usage`, getAuthHeaders());
      setSubscriptionUsage(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  const checkHardwareAccess = () => {
    if (!subscriptionUsage) return true; // Allow if not loaded yet
    if (!subscriptionUsage.features.hardware_access) {
      setUpgradeFeature("hardware");
      setShowUpgradeModal(true);
      return false;
    }
    return true;
  };

  const fetchDevices = async () => {
    try {
      const response = await axios.get(`${API_BASE}/hardware/devices`, getAuthHeaders());
      setDevices(response.data.devices);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  // Fetch In-Ear systems for interference detection
  const fetchSystems = async () => {
    try {
      const response = await axios.get(`${API_BASE}/systems`, getAuthHeaders());
      setSystems(response.data);
    } catch (error) {
      // Error handled silently
    }
  };

  // Check for RF interference with systems
  const checkInterference = useCallback((scanData) => {
    if (!scanData || scanData.length === 0 || systems.length === 0) return;
    
    const newAlerts = [];
    
    for (const system of systems) {
      const systemFreq = parseFloat(system.frequency);
      if (isNaN(systemFreq)) continue;
      
      // Find scan data points near this frequency (within 0.5 MHz)
      const nearbyPoints = scanData.filter(p => 
        Math.abs(p.frequency - systemFreq) <= 0.5
      );
      
      if (nearbyPoints.length === 0) continue;
      
      // Get the highest signal level near this frequency
      const maxLevel = Math.max(...nearbyPoints.map(p => p.level));
      
      // Check if signal is above threshold (interference detected)
      if (maxLevel > RF_ALERT_THRESHOLD) {
        newAlerts.push({
          system: system.name,
          frequency: systemFreq,
          channel: system.channel,
          signalLevel: maxLevel,
          severity: maxLevel > -60 ? 'critical' : maxLevel > -70 ? 'warning' : 'info',
          message: maxLevel > -60 
            ? `¡INTERFERENCIA CRÍTICA! Señal fuerte detectada`
            : maxLevel > -70 
              ? `Posible interferencia detectada`
              : `Actividad RF detectada`
        });
      }
    }
    
    setRfAlerts(newAlerts);
    
    // Show toast for critical alerts
    const criticalAlerts = newAlerts.filter(a => a.severity === 'critical');
    if (criticalAlerts.length > 0) {
      toast.error(`⚠️ ALERTA RF: ${criticalAlerts.length} sistema(s) con interferencia crítica!`);
    }
  }, [systems]);

  // Effect to check for interference when scan data updates
  useEffect(() => {
    if (liveData && liveData.length > 0) {
      checkInterference(liveData);
    }
  }, [liveData, checkInterference]);

  // ============ USB LIVE CONNECTION ============
  
  // USB connection - use context functions
  const connectUSB = async () => {
    await connectSerial();
  };

  const disconnectUSB = async () => {
    await disconnectSerial();
  };

  const startLiveScan = async () => {
    await startSerialScan();
  };
  
  const stopLiveScan = async () => {
    await stopSerialScan();
  };

  // Legacy startLiveScan_OLD - kept for reference but not used
  const startLiveScan_OLD = async () => {
    // Debug log removed
    
    setRawDataLog([{ time: new Date().toLocaleTimeString(), data: "🔄 Iniciando escaneo..." }]);
    
    if (!usbPort) {
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "❌ No hay puerto USB" }]);
      toast.error("Conecta el dispositivo USB primero");
      return;
    }

    if (!usbPort.readable || !usbPort.writable) {
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "❌ Puerto no listo (readable/writable null)" }]);
      toast.error("El puerto no está listo. Reconecta el dispositivo.");
      return;
    }

    // Release any existing locks
    try {
      if (writerRef.current) {
        writerRef.current.releaseLock();
        writerRef.current = null;
      }
      if (readerRef.current) {
        readerRef.current.releaseLock();
        readerRef.current = null;
      }
    } catch (e) {
      // Lock release error ignored
    }

    setLiveScanning(true);
    setDetectedDevice("tinySA Basic");
    
    try {
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "📡 Obteniendo writer/reader..." }]);
      
      const writer = usbPort.writable.getWriter();
      const reader = usbPort.readable.getReader();
      
      writerRef.current = writer;
      readerRef.current = reader;
      
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "✓ Writer/Reader OK" }]);
      
      let dataPoints = [];
      let buffer = "";

      // Read loop
      (async () => {
        setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "👂 Escuchando datos..." }]);
        try {
          while (true) {
            const { value, done } = await reader.read();
            if (done) {
              setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "⚠️ Reader done" }]);
              break;
            }
            
            const text = new TextDecoder().decode(value);
            buffer += text;
            
            // Check for end of data (ch> prompt)
            const hasPrompt = buffer.includes("ch>");
            
            const lines = buffer.split('\n');
            buffer = lines.pop() || "";
            
            for (const line of lines) {
              const t = line.trim();
              // Match scientific notation: -9.637500e+01 or -1.018750e+02
              if (/^-?\d+\.\d+e[+-]\d+$/i.test(t)) {
                dataPoints.push(parseFloat(t));
              }
            }
            
            // Update chart when we have data and see the prompt
            if (hasPrompt && dataPoints.length > 100) {
              const n = dataPoints.length;
              const step = 500 / (n - 1);
              const newData = dataPoints.map((v, i) => ({ 
                frequency: 480 + i * step, 
                level: v 
              }));
              setLiveData(newData);
              
              // Check for interference with systems
              checkInterference(newData);
              
              setRawDataLog(prev => [...prev.slice(-10), { 
                time: new Date().toLocaleTimeString(), 
                data: `✅ Gráfico actualizado: ${n} puntos`
              }]);
              dataPoints = [];
              buffer = ""; // Clear buffer after prompt
            }
          }
        } catch (e) {
          setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: `❌ Read error: ${e.message}` }]);
        }
      })();

      // Send commands
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "📤 Enviando comandos..." }]);
      await writer.write(new TextEncoder().encode("sweep 480000000 980000000\r"));
      await new Promise(r => setTimeout(r, 200));
      await writer.write(new TextEncoder().encode("data 0\r"));
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: "📤 Comandos enviados" }]);

      // Keep requesting
      scanIntervalRef.current = setInterval(async () => {
        try {
          await writer.write(new TextEncoder().encode("data 0\r"));
        } catch (e) {
          clearInterval(scanIntervalRef.current);
        }
      }, 1000);

    } catch (error) {
      // Error handled silently
      setRawDataLog(prev => [...prev, { time: new Date().toLocaleTimeString(), data: `❌ Error: ${error.message}` }]);
      toast.error("Error: " + error.message);
      setLiveScanning(false);
    }
  };

  const readUSBData = async () => {
    // Re-create reader if needed
    if (!readerRef.current && usbPort && usbPort.readable) {
      try {
        readerRef.current = usbPort.readable.getReader();
      } catch (e) {
        setRawDataLog(prev => [...prev, { 
          time: new Date().toLocaleTimeString(), 
          data: `ERROR creando reader: ${e.message}`
        }]);
        return;
      }
    }
    
    if (!readerRef.current) {
      setRawDataLog(prev => [...prev, { 
        time: new Date().toLocaleTimeString(), 
        data: "ERROR: No hay reader disponible"
      }]);
      return;
    }
    
    setRawDataLog(prev => [...prev, { 
      time: new Date().toLocaleTimeString(), 
      data: "✓ Reader iniciado, esperando datos..."
    }]);
    
    const decoder = new TextDecoder();
    let textBuffer = "";
    let binaryBuffer = [];

    try {
      while (liveScanning && readerRef.current) {
        let readResult;
        try {
          readResult = await readerRef.current.read();
        } catch (readError) {
          setRawDataLog(prev => [...prev.slice(-20), { 
            time: new Date().toLocaleTimeString(), 
            data: `ERROR read(): ${readError.message}`
          }]);
          break;
        }
        
        const { value, done } = readResult;
        
        if (done) {
          setRawDataLog(prev => [...prev.slice(-20), { 
            time: new Date().toLocaleTimeString(), 
            data: "Stream terminado (done=true)"
          }]);
          break;
        }
        
        if (!value || value.length === 0) continue;
        
        // Store raw bytes
        const rawBytes = Array.from(value);
        binaryBuffer = binaryBuffer.concat(rawBytes);
        
        // Log what we receive - ALWAYS log any data
        const preview = rawBytes.slice(0, 40).map(b => 
          b >= 32 && b < 127 ? String.fromCharCode(b) : `[${b.toString(16)}]`
        ).join('');
        setRawDataLog(prev => [...prev.slice(-20), { 
          time: new Date().toLocaleTimeString(), 
          data: `📥 ${rawBytes.length}B: ${preview}`
        }]);
        
        // Also try text processing
        const textChunk = decoder.decode(value, { stream: true });
        textBuffer += textChunk;
        
        // Process complete text lines
        const lines = textBuffer.split('\r');
        textBuffer = lines.pop() || "";
        
        for (const line of lines) {
          if (line.trim().length > 0) {
            parseRFExplorerData(line.trim());
          }
        }
        
        // Look for tinySA "data 0" response in binary buffer
        // Format: "data 0" (6 chars) + \r (1 byte) + binary data (251 bytes) + \r
        const dataHeader = [100, 97, 116, 97, 32, 48, 13]; // "data 0\r"
        const headerIndex = findSubarray(binaryBuffer, dataHeader);
        
        if (headerIndex >= 0) {
          const dataStart = headerIndex + dataHeader.length;
          // tinySA Basic has 251 points, Ultra has 450
          const possibleLengths = [251, 290, 450];
          
          for (const dataLen of possibleLengths) {
            if (binaryBuffer.length >= dataStart + dataLen) {
              const traceData = binaryBuffer.slice(dataStart, dataStart + dataLen);
              processTinySABinaryData(traceData);
              binaryBuffer = binaryBuffer.slice(dataStart + dataLen);
              break;
            }
          }
        }
        
        // Prevent buffer from growing too large
        if (binaryBuffer.length > 2000) {
          binaryBuffer = binaryBuffer.slice(-1000);
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Error handled silently
        setRawDataLog(prev => [...prev.slice(-20), { 
          time: new Date().toLocaleTimeString(), 
          data: `ERROR: ${error.message}`
        }]);
      }
    }
  };

  // Helper function to find subarray in array
  const findSubarray = (arr, subarr) => {
    for (let i = 0; i <= arr.length - subarr.length; i++) {
      let found = true;
      for (let j = 0; j < subarr.length; j++) {
        if (arr[i + j] !== subarr[j]) {
          found = false;
          break;
        }
      }
      if (found) return i;
    }
    return -1;
  };

  const processTinySABinaryData = (bytes) => {
    // tinySA "data 0" response: 
    // Header: "data 0\r" (7 bytes) + binary data (251 bytes for Basic, 450 for Ultra)
    // Each byte is a dBm value: 0 = -130 dBm, 255 = +15 dBm
    // Formula: dBm = (byte_value * 145 / 255) - 130
    
    const startFreq = 480;
    const endFreq = 980;
    const numPoints = bytes.length;
    const stepFreq = (endFreq - startFreq) / (numPoints - 1);
    
    const newData = [];
    let validPoints = 0;
    let minVal = 255, maxVal = 0;
    
    for (let i = 0; i < numPoints; i++) {
      const freq = startFreq + (i * stepFreq);
      // Convert byte to dBm: tinySA uses 0-255 range mapping to approximately -130 to +15 dBm
      const byteVal = bytes[i];
      const level = (byteVal * 145 / 255) - 130;
      
      if (byteVal > 0 && byteVal < 255) {
        validPoints++;
        minVal = Math.min(minVal, byteVal);
        maxVal = Math.max(maxVal, byteVal);
      }
      
      newData.push({ frequency: freq, level: level });
    }
    
    // Only update if we have valid-looking data
    if (validPoints > numPoints * 0.3) {
      setDetectedDevice("tinySA Basic");
      setLiveData(newData);
      
      setRawDataLog(prev => [...prev.slice(-20), { 
        time: new Date().toLocaleTimeString(), 
        data: `✓ DATOS: ${numPoints} pts, rango byte: ${minVal}-${maxVal}, dBm: ${((minVal*145/255)-130).toFixed(1)} a ${((maxVal*145/255)-130).toFixed(1)}`
      }]);
    }
  };

  const parseRFExplorerData = (line) => {
    // Log raw data for debugging (keep last 20 entries)
    if (line.length > 0 && !line.startsWith('ch>')) {
      setRawDataLog(prev => [...prev.slice(-20), { 
        time: new Date().toLocaleTimeString(), 
        data: line.substring(0, 80)
      }]);
    }

    // tinySA "data 0" response: scientific notation dBm values
    // Format: -8.706250e+01 (which is -87.0625 dBm)
    const scientificMatch = line.match(/^-?\d+\.\d+e[+-]\d+$/i);
    if (scientificMatch) {
      const dBmValue = parseFloat(line);
      if (!isNaN(dBmValue) && dBmValue < 0 && dBmValue > -150) {
        // Accumulate values - tinySA sends ~291 values per sweep
        setTinySADataBuffer(prev => {
          const newBuffer = [...prev, dBmValue];
          
          // When we have enough points (around 290), update the display
          if (newBuffer.length >= 290) {
            const startFreq = 480;
            const endFreq = 980;
            const numPoints = newBuffer.length;
            const stepFreq = (endFreq - startFreq) / (numPoints - 1);
            
            const newData = newBuffer.map((level, i) => ({
              frequency: startFreq + (i * stepFreq),
              level: level
            }));
            
            setDetectedDevice("tinySA Basic ✓");
            setLiveData(newData);
            
            // Clear buffer for next sweep
            return [];
          }
          return newBuffer;
        });
      }
      return;
    }

    // RF Explorer sweep data format: $S<startFreq>,<stepFreq>,<numSteps>,<data...>
    if (line.startsWith('$S')) {
      try {
        const parts = line.substring(2).split(',');
        if (parts.length >= 4) {
          const startFreq = parseFloat(parts[0]) / 1000; // kHz to MHz
          const stepFreq = parseFloat(parts[1]) / 1000;
          const numSteps = parseInt(parts[2]);
          
          const newData = [];
          for (let i = 0; i < numSteps && i + 3 < parts.length; i++) {
            const level = -parseInt(parts[i + 3]) / 2; // Convert to dBm
            const freq = startFreq + (i * stepFreq);
            if (freq >= 480 && freq <= 980) {
              newData.push({ frequency: freq, level: level });
            }
          }
          
          if (newData.length > 0) {
            setDetectedDevice("RF Explorer");
            setLiveData(prev => {
              const updated = [...prev];
              for (const point of newData) {
                const idx = updated.findIndex(p => Math.abs(p.frequency - point.frequency) < 0.25);
                if (idx >= 0) {
                  updated[idx] = point;
                }
              }
              return updated;
            });
          }
        }
      } catch (e) {
        // Error handled silently
      }
    }
    // TinySA scanraw response format: {freq} {value}
    else if (line.startsWith('{') || /^\d+\s+-?\d+/.test(line) || /^-?\d+(\.\d+)?\s+-?\d+/.test(line)) {
      try {
        const match = line.match(/\{?(-?\d+(?:\.\d+)?)\}?\s+\{?(-?\d+(?:\.\d+)?)\}?/);
        if (match) {
          let freq = parseFloat(match[1]);
          if (freq > 100000) {
            freq = freq / 1000000;
          }
          const level = parseFloat(match[2]);
          if (freq >= 480 && freq <= 980 && !isNaN(level)) {
            setDetectedDevice("tinySA/Relacart");
            setLiveData(prev => {
              const updated = [...prev];
              const idx = updated.findIndex(p => Math.abs(p.frequency - freq) < 0.25);
              if (idx >= 0) {
                updated[idx] = { frequency: freq, level: level };
              }
              return updated;
            });
          }
        }
      } catch (e) {
        // Error handled silently
      }
    }
  };

  // stopLiveScan is now defined above using context

  const saveLiveScan = async (customName = null) => {
    if (liveData.length === 0) {
      toast.error("No hay datos para guardar");
      return;
    }

    const validData = liveData.filter(d => d.level > -100);
    if (validData.length === 0) {
      toast.error("No se han recibido datos del analizador");
      return;
    }

    const frequencies = validData.map(d => d.frequency);
    const bandMin = Math.min(...frequencies);
    const bandMax = Math.max(...frequencies);

    setSavingScan(true);
    try {
      const scanNameToUse = customName || scanName || `Escaneo USB - ${new Date().toLocaleString()}`;
      await axios.post(`${API_BASE}/scans`, {
        name: scanNameToUse,
        band_min: bandMin,
        band_max: bandMax,
        scan_data: validData.map(d => ({
          frequency: d.frequency,
          level: d.level,
          timestamp: new Date().toISOString()
        })),
      }, getAuthHeaders());
      
      toast.success(`Escaneo "${scanNameToUse}" guardado exitosamente`);
      setShowSaveScanDialog(false);
      setScanName("");
    } catch (error) {
      toast.error("Error al guardar escaneo");
    } finally {
      setSavingScan(false);
    }
  };

  const handleSaveScanClick = () => {
    if (liveData.length === 0 || liveData.filter(d => d.level > -100).length === 0) {
      toast.error("No hay datos válidos para guardar");
      return;
    }
    setScanName(`Escaneo ${detectedDevice} - ${new Date().toLocaleDateString()}`);
    setShowSaveScanDialog(true);
  };

  // Export scan data to CSV file
  const exportScanToCSV = () => {
    const validData = liveData.filter(d => d.level > -100);
    if (validData.length === 0) {
      toast.error("No hay datos válidos para exportar");
      return;
    }

    // Create CSV content with headers
    const csvHeader = "frequency_mhz,level_dbm,timestamp\n";
    const csvRows = validData.map(d => 
      `${d.frequency.toFixed(3)},${d.level.toFixed(1)},${new Date().toISOString()}`
    ).join("\n");
    const csvContent = csvHeader + csvRows;

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const filename = scanName 
      ? `${scanName.replace(/[^a-zA-Z0-9\-_]/g, "_")}.csv`
      : `escaneo_rf_${new Date().toISOString().split("T")[0]}.csv`;
    
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Escaneo exportado a CSV");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImportData(e.target.result);
      setImportName(file.name.replace(/\.[^/.]+$/, ""));
    };
    reader.readAsText(file);
  };

  const handleImportScan = async () => {
    if (!importData.trim()) {
      toast.error("No hay datos para importar");
      return;
    }
    
    const parseResult = parseScanData(importData);
    const parsedData = parseResult.data;
    
    if (parsedData.length === 0) {
      // Show more detailed error
      let errorMsg = "No se pudieron parsear los datos.";
      if (parseResult.parseErrors.length > 0) {
        errorMsg += ` ${parseResult.parseErrors.length} líneas con errores.`;
        // Show first error as hint
        if (parseResult.parseErrors[0]) {
          errorMsg += ` Ejemplo: ${parseResult.parseErrors[0]}`;
        }
      }
      toast.error(errorMsg, { duration: 6000 });
      return;
    }
    
    // Show info for Workbench format
    if (parseResult.isWorkbenchFormat) {
      toast.info("Detectado formato Wireless Workbench - Importando frecuencias asignadas", { duration: 4000 });
    }
    
    // Show warning if some lines were skipped
    if (parseResult.skippedLines > 0) {
      toast.warning(`${parseResult.skippedLines} líneas ignoradas (formato incorrecto o fuera de rango)`, { duration: 4000 });
    }
    
    // Calculate band range from data
    const frequencies = parsedData.map(d => d.frequency);
    const bandMin = Math.min(...frequencies);
    const bandMax = Math.max(...frequencies);
    
    try {
      // Save to backend
      const response = await axios.post(`${API_BASE}/scans`, {
        name: importName || `Escaneo USB - ${new Date().toLocaleString()}`,
        band_min: bandMin,
        band_max: bandMax,
        scan_data: parsedData,
      }, getAuthHeaders());
      
      // Show result
      setScanResult({
        scan_id: response.data.id,
        device_id: "usb-import",
        points_count: parsedData.length,
        band_range: `${bandMin.toFixed(1)}-${bandMax.toFixed(1)} MHz`,
        scan_data: parsedData
      });
      
      toast.success(`Escaneo importado: ${parsedData.length} puntos (${bandMin.toFixed(1)}-${bandMax.toFixed(1)} MHz)`);
      setShowImportDialog(false);
      setImportData("");
      setImportName("");
    } catch (error) {
      toast.error("Error al guardar el escaneo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        device_type: form.device_type,
        device_name: form.device_name,
        connection_type: form.connection_type,
        ip_address: form.connection_type === "network" ? form.ip_address : null,
        port: form.connection_type === "network" ? parseInt(form.port) : null,
        serial_port: form.connection_type === "serial" || form.connection_type === "usb" ? form.serial_port : null,
        baud_rate: form.connection_type === "serial" || form.connection_type === "usb" ? parseInt(form.baud_rate) : null,
      };

      await axios.post(`${API_BASE}/hardware/register`, data, getAuthHeaders());
      toast.success("Dispositivo registrado");
      setShowDialog(false);
      setForm({
        device_type: "rf_analyzer",
        device_name: "",
        connection_type: "usb",
        ip_address: "",
        port: "5000",
        serial_port: "",
        baud_rate: "9600",
      });
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar dispositivo");
    }
  };

  const handleConnect = async (deviceId) => {
    try {
      const response = await axios.post(`${API_BASE}/hardware/${deviceId}/connect`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchDevices();
    } catch (error) {
      toast.error("Error al conectar");
    }
  };

  const handleScan = async (device) => {
    // For USB devices, open import dialog instead of simulated scan
    if (device.connection_type === "usb" || device.connection_type === "serial") {
      setShowImportDialog(true);
      toast.info("Importa los datos del escaneo realizado con tu analizador RF");
      return;
    }
    
    setScanning(device.id);
    setScanResult(null);
    try {
      const response = await axios.post(
        `${API_BASE}/hardware/${device.id}/scan`,
        {
          device_id: device.id,
          band_min: 480,
          band_max: 980,
          step_size: 0.5,  // 500 kHz - Anleon S3D standard
        },
        getAuthHeaders()
      );
      setScanResult(response.data);
      toast.success(`Escaneo completado: ${response.data.points_count} puntos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error durante el escaneo");
    } finally {
      setScanning(null);
    }
  };

  const handleDelete = async (deviceId) => {
    try {
      await axios.delete(`${API_BASE}/hardware/${deviceId}`, getAuthHeaders());
      toast.success("Dispositivo eliminado");
      fetchDevices();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Hardware RF
          </h1>
          <p className="text-sm text-[#71717A]">
            Conexión con analizadores RF y distribuidores de antena
          </p>
        </div>
        <div className="flex gap-2">
          <SystemsToggleButton
            systemCount={systems.length}
            isActive={showSystemsView}
            onClick={() => setShowSystemsView(!showSystemsView)}
          />
          <Button
            onClick={() => setShowImportDialog(true)}
            className="btn-tactical btn-tactical-secondary"
            data-testid="import-scan-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar Archivo
          </Button>
          <Button
            onClick={() => setShowDialog(true)}
            className="btn-tactical btn-tactical-primary"
            data-testid="add-hardware-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Dispositivo
          </Button>
        </div>
      </div>

      {/* Main Tabs: USB Local vs Control RF en Red */}
      <Tabs defaultValue="usb" className="w-full">
        <TabsList className="w-full max-w-md bg-[#0A0A0A] border border-white/[0.05] mb-6">
          <TabsTrigger 
            value="usb" 
            className="flex-1 data-[state=active]:bg-[#34C759] data-[state=active]:text-black"
          >
            <Usb className="w-4 h-4 mr-2" />
            USB Local
          </TabsTrigger>
          {isFeatureAvailable('rf_network_control') && (
            <TabsTrigger 
              value="rf-network" 
              className="flex-1 data-[state=active]:bg-[#007AFF] data-[state=active]:text-white"
              data-testid="rf-network-tab"
            >
              <Wifi className="w-4 h-4 mr-2" />
              Control RF en Red
            </TabsTrigger>
          )}
        </TabsList>

        {/* USB Local Tab Content */}
        <TabsContent value="usb">
          {/* Relacart DISCOVER WebHID Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          {/* Placeholder for future live spectrum from Relacart */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5 h-full">
            <h2 className="font-heading font-semibold text-[#F4F4F5] mb-4">
              Espectro en Tiempo Real
            </h2>
            <p className="text-sm text-[#71717A]">
              {hidConnected 
                ? `${hidDevice?.name || 'Dispositivo'} conectado - ${hidScanning ? 'Escaneando...' : 'Listo'}`
                : "Conecta un analizador RF para ver el espectro en vivo"
              }
            </p>
          </div>
        </div>
        
        {/* Global USB Connection Panel - Persists across pages */}
        <div className="lg:col-span-1">
          <RelacartConnectionPanel
            hidConnected={hidConnected}
            hidDevice={hidDevice}
            hidDeviceInfo={hidDeviceInfo}
            hidScanning={hidScanning}
            isHIDSupported={isHIDSupported}
            connectHID={connectHID}
            disconnectHID={disconnectHID}
            startHIDScan={startHIDScan}
            stopHIDScan={stopHIDScan}
            globalRawDataLog={globalRawDataLog}
            onImportClick={() => setShowImportDialog(true)}
          />
        </div>
      </div>

      {/* USB Live Scan Section - tinySA LOCAL */}
      <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
              usbConnected ? "bg-[#34C759]/20" : "bg-[#71717A]/20"
            }`}>
              <Usb className={`w-5 h-5 ${usbConnected ? "text-[#34C759]" : "text-[#71717A]"}`} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-[#F4F4F5]">
                Escaneo USB en Vivo (tinySA)
              </h2>
              <p className="text-xs text-[#71717A]">
                {usbConnected ? "Analizador RF conectado" : "Conecta tu analizador RF por USB"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Marker Config Button - Always visible */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#AF52DE]/50 text-[#AF52DE] hover:bg-[#AF52DE]/10 text-xs"
                  data-testid="marker-config-button-header"
                >
                  <Palette className="w-3 h-3 mr-1" />
                  Colores
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-96 bg-[#111111] border-white/10 p-0"
                align="end"
              >
                <div className="p-3 border-b border-white/10">
                  <h4 className="font-semibold text-white text-sm flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#AF52DE]" />
                    Marcadores de Frecuencia
                  </h4>
                  <p className="text-xs text-[#71717A] mt-1">
                    Personaliza colores y etiquetas del espectro
                  </p>
                </div>
                
                {/* Global Settings */}
                <div className="p-3 border-b border-white/10 space-y-2">
                  <p className="text-xs text-[#A1A1AA] font-medium mb-2">Mostrar en etiquetas:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={globalMarkerSettings.showName}
                        onCheckedChange={(checked) => 
                          setGlobalMarkerSettings(prev => ({ ...prev, showName: checked }))
                        }
                        className="scale-75"
                      />
                      <span className="text-xs text-[#A1A1AA]">Nombre</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={globalMarkerSettings.showChannel}
                        onCheckedChange={(checked) => 
                          setGlobalMarkerSettings(prev => ({ ...prev, showChannel: checked }))
                        }
                        className="scale-75"
                      />
                      <span className="text-xs text-[#A1A1AA]">Canal</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={globalMarkerSettings.showFrequency}
                        onCheckedChange={(checked) => 
                          setGlobalMarkerSettings(prev => ({ ...prev, showFrequency: checked }))
                        }
                        className="scale-75"
                      />
                      <span className="text-xs text-[#A1A1AA]">Freq</span>
                    </label>
                  </div>
                </div>
                
                {/* System List */}
                <div className="max-h-64 overflow-y-auto">
                  {systems.length === 0 ? (
                    <div className="p-4 text-center">
                      <Radio className="w-6 h-6 text-[#52525B] mx-auto mb-2" />
                      <p className="text-xs text-[#71717A]">No hay sistemas</p>
                    </div>
                  ) : (
                    systems.slice(0, 20).map((system) => {
                      const config = markerConfig[system.id] || {};
                      const currentColor = config.color || getMarkerColor(system);
                      const isHidden = config.hidden === true;
                      
                      return (
                        <div 
                          key={system.id}
                          className={`p-2 border-b border-white/5 flex items-center gap-2 ${
                            isHidden ? 'opacity-50' : ''
                          }`}
                        >
                          {/* Color picker */}
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className="w-5 h-5 rounded border border-white/20 flex-shrink-0"
                                style={{ backgroundColor: currentColor }}
                                title="Cambiar color"
                              />
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2 bg-[#1A1A1A] border-white/10" side="left">
                              <div className="grid grid-cols-5 gap-1">
                                {MARKER_COLORS.map(color => (
                                  <button
                                    key={color.value}
                                    onClick={() => updateMarkerConfig(system.id, { color: color.value })}
                                    className={`w-5 h-5 rounded border-2 ${
                                      currentColor === color.value 
                                        ? 'border-white' 
                                        : 'border-transparent'
                                    }`}
                                    style={{ backgroundColor: color.value }}
                                    title={color.name}
                                  />
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          
                          {/* System info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white truncate">
                              {system.name || `CH ${system.channel}`}
                            </p>
                            <p className="text-[10px] font-mono" style={{ color: currentColor }}>
                              {parseFloat(system.frequency).toFixed(3)} MHz
                            </p>
                          </div>
                          
                          {/* Visibility toggle */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateMarkerConfig(system.id, { hidden: !isHidden })}
                            className={`h-5 w-5 p-0 ${isHidden ? 'text-[#52525B]' : 'text-[#34C759]'}`}
                            title={isHidden ? 'Mostrar' : 'Ocultar'}
                          >
                            {isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </Button>
                        </div>
                      );
                    })
                  )}
                  {systems.length > 20 && (
                    <div className="p-2 text-center text-xs text-[#71717A]">
                      +{systems.length - 20} más
                    </div>
                  )}
                </div>
                
                {/* Reset button */}
                {Object.keys(markerConfig).length > 0 && (
                  <div className="p-2 border-t border-white/10">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMarkerConfig({})}
                      className="w-full text-xs text-[#71717A] hover:text-white"
                    >
                      Restablecer colores
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            
            {!serialAllowed ? (
              <Button
                onClick={() => setShowImportDialog(true)}
                className="btn-tactical btn-tactical-primary"
              >
                <Upload className="w-4 h-4 mr-2" />
                Importar Archivo
              </Button>
            ) : !usbConnected ? (
              <Button
                onClick={() => {
                  if (checkHardwareAccess()) {
                    connectUSB();
                  }
                }}
                className="btn-tactical btn-tactical-primary"
                data-testid="connect-usb-button"
              >
                <Usb className="w-4 h-4 mr-2" />
                Conectar USB
                {subscriptionUsage && !subscriptionUsage.features.hardware_access && (
                  <Lock className="w-3 h-3 ml-2 text-yellow-400" />
                )}
              </Button>
            ) : (
              <>
                {!liveScanning ? (
                  <Button
                    onClick={startLiveScan}
                    className="btn-tactical btn-tactical-primary"
                    data-testid="start-live-scan-button"
                  >
                    <Radio className="w-4 h-4 mr-2" />
                    Iniciar Escaneo
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={stopLiveScan}
                      className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
                      data-testid="stop-live-scan-button"
                    >
                      <StopCircle className="w-4 h-4 mr-2" />
                      Detener
                    </Button>
                    <Button
                      onClick={handleSaveScanClick}
                      disabled={liveData.filter(d => d.level > -100).length === 0}
                      className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
                      data-testid="save-scan-button"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Guardar
                    </Button>
                    {/* Broadcast to iPad/mobile */}
                    <BroadcastControls isScanning={liveScanning} spectrumData={liveData} />
                  </>
                )}
                <Button
                  onClick={disconnectUSB}
                  variant="ghost"
                  className="text-[#FF3B30] hover:text-[#FF3B30]"
                  data-testid="disconnect-usb-button"
                >
                  Desconectar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* USB Troubleshooting Collapsible */}
        {serialAllowed && !usbConnected && <USBTroubleshooting />}

        {/* Serial Blocked Warning - Show Remote Viewer option for iPad/mobile */}
        {!serialAllowed && (
          <MobileDeviceWarning
            onOpenRemoteViewer={() => setShowRemoteViewer(true)}
            onOpenImport={() => setShowImportDialog(true)}
          />
        )}

        {/* Remote Viewer Section for iPad/Mobile */}
        {showRemoteViewer && (
          <div className="mb-6">
            <RemoteSpectrumViewer onClose={() => setShowRemoteViewer(false)} />
          </div>
        )}

        {/* Live Spectrum Display - show when connected OR when there's scan data */}
        {(usbConnected || liveData.length > 0) && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${liveScanning ? "bg-[#FF3B30] animate-pulse" : liveData.length > 0 ? "bg-[#34C759]" : "bg-[#71717A]"}`} />
                  <span className="text-xs text-[#71717A]">
                    {liveScanning ? "ESCANEANDO EN VIVO" : liveData.length > 0 ? "DATOS DISPONIBLES" : "LISTO"}
                  </span>
                </div>
                <span className="text-xs text-[#71717A]">
                  {liveData.filter(d => d.level > -100).length} puntos | 480-980 MHz
                </span>
                
                {/* Noise Floor Control */}
                <div className="flex items-center gap-2 ml-4 px-3 py-1 bg-[#1A1A1A] rounded-sm border border-white/[0.08]">
                  <span className="text-xs text-[#71717A]">Piso Ruido:</span>
                  <input
                    type="range"
                    min="-120"
                    max="-50"
                    value={noiseFloorLevel}
                    onChange={(e) => setNoiseFloorLevel(parseInt(e.target.value))}
                    className="w-20 h-1 bg-[#2A2A2A] rounded-lg appearance-none cursor-pointer accent-[#FF3B30]"
                    data-testid="noise-floor-slider-live"
                  />
                  <span className="text-xs text-[#FF3B30] font-mono w-12">{noiseFloorLevel} dB</span>
                </div>
                
                {/* Marker Config Button */}
                <Popover open={showMarkerPanel} onOpenChange={setShowMarkerPanel}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-2 border-[#AF52DE]/50 text-[#AF52DE] hover:bg-[#AF52DE]/10 text-xs"
                      data-testid="marker-config-button"
                    >
                      <Palette className="w-3 h-3 mr-1" />
                      Marcadores
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-96 bg-[#111111] border-white/10 p-0"
                    align="end"
                  >
                    <div className="p-3 border-b border-white/10">
                      <h4 className="font-semibold text-white text-sm flex items-center gap-2">
                        <Tag className="w-4 h-4 text-[#AF52DE]" />
                        Configurar Marcadores de Frecuencia
                      </h4>
                      <p className="text-xs text-[#71717A] mt-1">
                        Personaliza colores y etiquetas para cada sistema
                      </p>
                    </div>
                    
                    {/* Global Settings */}
                    <div className="p-3 border-b border-white/10 space-y-2">
                      <p className="text-xs text-[#A1A1AA] font-medium mb-2">Mostrar en etiquetas:</p>
                      <div className="grid grid-cols-3 gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={globalMarkerSettings.showName}
                            onCheckedChange={(checked) => 
                              setGlobalMarkerSettings(prev => ({ ...prev, showName: checked }))
                            }
                            className="scale-75"
                          />
                          <span className="text-xs text-[#A1A1AA]">Nombre</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={globalMarkerSettings.showChannel}
                            onCheckedChange={(checked) => 
                              setGlobalMarkerSettings(prev => ({ ...prev, showChannel: checked }))
                            }
                            className="scale-75"
                          />
                          <span className="text-xs text-[#A1A1AA]">Canal</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={globalMarkerSettings.showFrequency}
                            onCheckedChange={(checked) => 
                              setGlobalMarkerSettings(prev => ({ ...prev, showFrequency: checked }))
                            }
                            className="scale-75"
                          />
                          <span className="text-xs text-[#A1A1AA]">Frecuencia</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* System List */}
                    <div className="max-h-64 overflow-y-auto">
                      {systems.length === 0 ? (
                        <div className="p-4 text-center">
                          <Radio className="w-6 h-6 text-[#52525B] mx-auto mb-2" />
                          <p className="text-xs text-[#71717A]">No hay sistemas creados</p>
                        </div>
                      ) : (
                        systems.map((system) => {
                          const config = markerConfig[system.id] || {};
                          const currentColor = config.color || getMarkerColor(system);
                          const isHidden = config.hidden === true;
                          
                          return (
                            <div 
                              key={system.id}
                              className={`p-2 border-b border-white/5 flex items-center gap-2 ${
                                isHidden ? 'opacity-50' : ''
                              }`}
                            >
                              {/* Color picker */}
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    className="w-6 h-6 rounded border border-white/20 flex-shrink-0"
                                    style={{ backgroundColor: currentColor }}
                                    title="Cambiar color"
                                  />
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2 bg-[#1A1A1A] border-white/10">
                                  <div className="grid grid-cols-5 gap-1">
                                    {MARKER_COLORS.map(color => (
                                      <button
                                        key={color.value}
                                        onClick={() => updateMarkerConfig(system.id, { color: color.value })}
                                        className={`w-6 h-6 rounded border-2 ${
                                          currentColor === color.value 
                                            ? 'border-white' 
                                            : 'border-transparent'
                                        }`}
                                        style={{ backgroundColor: color.value }}
                                        title={color.name}
                                      />
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                              
                              {/* System info */}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-white truncate">
                                  {system.name || `Sistema ${system.channel}`}
                                </p>
                                <p className="text-[10px] font-mono" style={{ color: currentColor }}>
                                  {parseFloat(system.frequency).toFixed(3)} MHz
                                </p>
                              </div>
                              
                              {/* Visibility toggle */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateMarkerConfig(system.id, { hidden: !isHidden })}
                                className={`h-6 w-6 p-0 ${isHidden ? 'text-[#52525B]' : 'text-[#34C759]'}`}
                                title={isHidden ? 'Mostrar' : 'Ocultar'}
                              >
                                {isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </Button>
                            </div>
                          );
                        })
                      )}
                    </div>
                    
                    {/* Reset button */}
                    {Object.keys(markerConfig).length > 0 && (
                      <div className="p-2 border-t border-white/10">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setMarkerConfig({})}
                          className="w-full text-xs text-[#71717A] hover:text-white"
                        >
                          Restablecer todos los colores
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="h-64 w-full relative">
              {/* Loading overlay when scanning but no data yet */}
              {liveScanning && liveData.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/80 z-10 rounded">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-[#FF3B30] animate-spin mx-auto mb-2" />
                    <p className="text-sm text-[#71717A]">Recibiendo datos del tinySA...</p>
                    <p className="text-xs text-[#52525B] mt-1">El escaneo continúa aunque cambies de página</p>
                  </div>
                </div>
              )}
              
              {/* Empty state when not scanning and no data */}
              {!liveScanning && liveData.length === 0 && usbConnected && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/50 z-10 rounded">
                  <div className="text-center">
                    <Radio className="w-8 h-8 text-[#52525B] mx-auto mb-2" />
                    <p className="text-sm text-[#71717A]">Presiona "Iniciar Escaneo" para comenzar</p>
                  </div>
                </div>
              )}
              
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={liveData.length > 0 ? liveData.map(point => {
                    // Check if this frequency has an alert
                    const alert = rfAlerts.find(a => 
                      Math.abs(parseFloat(a.frequency) - point.frequency) < 1.5
                    );
                    return {
                      ...point,
                      alertColor: alert 
                        ? (alert.severity === 'critical' ? '#FF3B30' : '#FFCC00')
                        : null,
                      alertLevel: alert ? point.level : null
                    };
                  }) : [{ frequency: 480, level: -100 }, { frequency: 980, level: -100 }]}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="liveGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={liveScanning ? "#FF3B30" : "#34C759"} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={liveScanning ? "#FF3B30" : "#34C759"} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="alertGradientRed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FF3B30" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#FF3B30" stopOpacity={0.2} />
                    </linearGradient>
                    <linearGradient id="alertGradientYellow" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FFCC00" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#FFCC00" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis
                    dataKey="frequency"
                    tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    tickFormatter={(v) => `${v.toFixed(0)}`}
                    interval={Math.floor(liveData.length / 10)}
                    domain={[480, 980]}
                  />
                  <YAxis
                    tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                    tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                    domain={[-100, -20]}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={noiseFloorLevel} stroke="#FF3B30" strokeDasharray="5 5" strokeOpacity={0.5} />
                  <ReferenceLine y={-50} stroke="#FFCC00" strokeDasharray="5 5" strokeOpacity={0.3} />
                  
                  {/* LTE Band Warning Zone (698-806 MHz) */}
                  <ReferenceLine
                    x={698}
                    stroke="#FF3B30"
                    strokeOpacity={0.3}
                    label={{
                      value: "LTE",
                      fill: "#FF3B30",
                      fontSize: 9,
                      position: "top",
                    }}
                  />
                  <ReferenceLine
                    x={806}
                    stroke="#FF3B30"
                    strokeOpacity={0.3}
                  />
                  
                  {/* Reference lines for system frequencies - with custom colors and labels */}
                  {systems.map((system, idx) => {
                    const config = markerConfig[system.id] || {};
                    const isHidden = config.hidden === true;
                    
                    if (isHidden) return null;
                    
                    // Check if this system has an alert
                    const alert = rfAlerts.find(a => 
                      Math.abs(parseFloat(a.frequency) - parseFloat(system.frequency)) < 0.5
                    );
                    
                    // Use custom color or alert color
                    let lineColor = config.color || getMarkerColor(system);
                    if (alert && !config.color) {
                      if (alert.severity === 'critical') lineColor = "#FF3B30";
                      else if (alert.severity === 'warning') lineColor = "#FFCC00";
                    }
                    
                    // Build label based on settings
                    const showName = config.showName ?? globalMarkerSettings.showName;
                    const showFreq = config.showFrequency ?? globalMarkerSettings.showFrequency;
                    const showCh = config.showChannel ?? globalMarkerSettings.showChannel;
                    
                    let labelParts = [];
                    if (showName && system.name) labelParts.push(system.name.substring(0, 8));
                    if (showCh && system.channel) labelParts.push(`CH${system.channel}`);
                    if (showFreq) labelParts.push(`${parseFloat(system.frequency).toFixed(3)}`);
                    
                    const labelText = labelParts.join(' | ') || system.name?.substring(0, 5);
                    
                    return (
                      <ReferenceLine 
                        key={system.id || idx}
                        x={parseFloat(system.frequency)} 
                        stroke={lineColor}
                        strokeDasharray={alert ? "0" : "0"} 
                        strokeWidth={config.lineWidth || globalMarkerSettings.lineWidth}
                        strokeOpacity={config.opacity || globalMarkerSettings.opacity}
                        label={{ 
                          value: alert ? `⚠️ ${labelText}` : labelText, 
                          fill: lineColor, 
                          fontSize: 10,
                          position: 'top',
                          fontWeight: alert ? 'bold' : 'normal',
                          fontFamily: 'JetBrains Mono, monospace'
                        }}
                      />
                    );
                  })}
                  
                  {/* Alert markers - highlight problem frequencies */}
                  {rfAlerts.map((alert, idx) => (
                    <ReferenceLine 
                      key={`alert-${idx}`}
                      x={parseFloat(alert.frequency)}
                      stroke={alert.severity === 'critical' ? "#FF3B30" : "#FFCC00"}
                      strokeWidth={3}
                      strokeOpacity={0.8}
                    />
                  ))}
                  
                  {/* Main spectrum area */}
                  <Area
                    type="monotone"
                    dataKey="level"
                    stroke={liveScanning ? "#FF3B30" : "#34C759"}
                    fill="url(#liveGradient)"
                    strokeWidth={1.5}
                    isAnimationActive={false}
                  />
                  
                  {/* Overlay bars for alert frequencies - shows as colored bars on top */}
                  <Bar
                    dataKey="alertLevel"
                    fill="#FF3B30"
                    opacity={0.8}
                    isAnimationActive={false}
                  >
                    {liveData.length > 0 && liveData.map((entry, index) => {
                      const alert = rfAlerts.find(a => 
                        Math.abs(parseFloat(a.frequency) - entry.frequency) < 1.5
                      );
                      return (
                        <Cell 
                          key={`cell-${index}`}
                          fill={alert 
                            ? (alert.severity === 'critical' ? '#FF3B30' : '#FFCC00')
                            : 'transparent'
                          }
                        />
                      );
                    })}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            
            {/* RF Alerts Panel */}
            {rfAlerts.length > 0 && (
              <div className="mt-4 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/50 rounded-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Radio className="w-5 h-5 text-[#FF3B30] animate-pulse" />
                  <h3 className="font-bold text-[#FF3B30]">⚠️ ALERTAS DE INTERFERENCIA RF</h3>
                </div>
                <div className="space-y-2">
                  {rfAlerts.map((alert) => (
                    <div 
                      key={`${alert.system}-${alert.frequency}-${alert.channel}`}
                      className={`p-3 rounded border ${
                        alert.severity === 'critical' 
                          ? 'bg-[#FF3B30]/20 border-[#FF3B30]' 
                          : alert.severity === 'warning'
                            ? 'bg-[#FFCC00]/20 border-[#FFCC00]'
                            : 'bg-[#34C759]/20 border-[#34C759]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className={`font-bold ${
                            alert.severity === 'critical' ? 'text-[#FF3B30]' : 
                            alert.severity === 'warning' ? 'text-[#FFCC00]' : 'text-[#34C759]'
                          }`}>
                            {alert.system}
                          </span>
                          <span className={`text-sm ml-2 font-mono font-semibold ${
                            alert.severity === 'critical' ? 'text-[#FF3B30]' : 
                            alert.severity === 'warning' ? 'text-[#FFCC00]' : 'text-[#34C759]'
                          }`}>
                            Canal {alert.channel} | ⚡ {alert.frequency.toFixed(3)} MHz
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`font-mono font-bold ${
                            alert.severity === 'critical' ? 'text-[#FF3B30]' : 
                            alert.severity === 'warning' ? 'text-[#FFCC00]' : 'text-[#34C759]'
                          }`}>
                            {alert.signalLevel.toFixed(1)} dBm
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-[#A1A1AA] mt-1">{alert.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Systems being monitored */}
            {systems.length > 0 && liveScanning && rfAlerts.length === 0 && (
              <div className="mt-4 p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#34C759]" />
                  <span className="text-sm text-[#34C759]">
                    Monitoreando {systems.length} sistema(s) In-Ear - Sin interferencias detectadas
                  </span>
                </div>
              </div>
            )}
            
            {/* Coordination Mode Panel */}
            <div className="mt-4">
              <CoordinationModePanel />
            </div>
            
            {/* Debug panel */}
            <div className="mt-4 pt-4 border-t border-white/[0.08]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-4">
                  <span className="text-xs text-[#71717A]">Dispositivo detectado:</span>
                  <span className="text-xs text-[#34C759] font-mono">{detectedDevice}</span>
                </div>
                <Button
                  onClick={() => setShowDebug(!showDebug)}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-[#71717A]"
                >
                  {showDebug ? "Ocultar Debug" : "Mostrar Debug"}
                </Button>
              </div>
              
              {showDebug && (
                <div className="bg-black/50 rounded p-3 mt-2 max-h-40 overflow-y-auto font-mono text-xs">
                  <p className="text-[#FFCC00] mb-2">Datos raw recibidos (últimos 20):</p>
                  {rawDataLog.length === 0 ? (
                    <p className="text-[#71717A]">Esperando datos del dispositivo...</p>
                  ) : (
                    rawDataLog.map((entry) => (
                      <div key={`${entry.time}-${entry.data.substring(0, 20)}`} className="text-[#A1A1AA] mb-1">
                        <span className="text-[#71717A]">[{entry.time}]</span> {entry.data}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {!usbConnected && !isWebSerialSupported() && (
          <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm mt-4">
            <p className="text-sm text-[#FF3B30]">
              Tu navegador no soporta Web Serial API. Usa Google Chrome o Microsoft Edge para escaneo USB en vivo.
            </p>
          </div>
        )}

        {!usbConnected && isWebSerialSupported() && (
          <div className="p-4 bg-[#0A0A0A] border border-white/[0.08] rounded-sm mt-4">
            <p className="text-sm text-[#A1A1AA] mb-3">Dispositivos compatibles:</p>
            <div>
              <p className="text-xs text-[#34C759] font-medium mb-2">tinySA</p>
              <ul className="text-xs text-[#71717A] space-y-1">
                <li>• tinySA Basic</li>
                <li>• tinySA Ultra</li>
                <li>• tinySA4 (con modo USB)</li>
              </ul>
            </div>
            
            {/* Remote viewer option */}
            <div className="mt-3 pt-3 border-t border-white/[0.08]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#007AFF] font-medium">Ver desde iPad/Móvil</p>
                  <p className="text-xs text-[#71717A]">Visualiza el espectro de un tinySA conectado a otra PC</p>
                </div>
                <Button
                  onClick={() => setShowRemoteViewer(true)}
                  variant="outline"
                  size="sm"
                  className="border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 text-xs"
                  data-testid="open-remote-viewer-desktop"
                >
                  <Smartphone className="w-3 h-3 mr-1" />
                  Visor Remoto
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-[#71717A]">Cargando...</p>
        </div>
      ) : devices.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-[#71717A]">
            También puedes registrar dispositivos para referencia
          </p>
          <Button
            onClick={() => setShowDialog(true)}
            variant="ghost"
            className="mt-2 text-[#34C759]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Dispositivo
          </Button>
        </div>
      ) : (
        <>
          <h3 className="font-heading font-semibold text-[#F4F4F5] mb-4">Dispositivos Registrados</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hardware-grid">
          {devices.map((device) => (
            <div
              key={device.id}
              className="bg-[#111111] border border-white/[0.08] rounded-sm p-5"
              data-testid={`hardware-card-${device.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                    device.is_connected ? "bg-[#34C759]/20" : "bg-[#71717A]/20"
                  }`}>
                    <Cpu className={`w-5 h-5 ${
                      device.is_connected ? "text-[#34C759]" : "text-[#71717A]"
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-[#F4F4F5]">
                      {device.device_name}
                    </h3>
                    <p className="text-xs text-[#71717A] capitalize">
                      {device.device_type.replace("_", " ")}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(device.id)}
                  className="h-8 w-8 p-0 text-[#FF3B30] hover:text-[#FF3B30]"
                  data-testid={`delete-hardware-${device.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#71717A]">Conexión:</span>
                  <span className="text-[#F4F4F5] capitalize">{device.connection_type}</span>
                </div>
                {device.ip_address && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#71717A]">IP:</span>
                    <span className="font-mono text-[#F4F4F5]">{device.ip_address}:{device.port}</span>
                  </div>
                )}
                {device.serial_port && (
                  <div className="flex items-center justify-between">
                    <span className="text-[#71717A]">Puerto:</span>
                    <span className="font-mono text-[#F4F4F5]">{device.serial_port}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[#71717A]">Estado:</span>
                  <span className={`flex items-center gap-1 ${
                    device.is_connected ? "text-[#34C759]" : "text-[#71717A]"
                  }`}>
                    {device.is_connected ? (
                      <><Wifi className="w-3 h-3" /> Conectado</>
                    ) : (
                      <><WifiOff className="w-3 h-3" /> Desconectado</>
                    )}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                {(device.connection_type === "usb" || device.connection_type === "serial") ? (
                  <Button
                    onClick={() => setShowImportDialog(true)}
                    variant="ghost"
                    size="sm"
                    className="flex-1 btn-tactical btn-tactical-primary text-xs"
                    data-testid={`import-hardware-${device.id}`}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    Importar Escaneo
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => handleConnect(device.id)}
                      variant="ghost"
                      size="sm"
                      className="flex-1 btn-tactical btn-tactical-secondary text-xs"
                      data-testid={`connect-hardware-${device.id}`}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      {device.is_connected ? "Reconectar" : "Conectar"}
                    </Button>
                    <Button
                      onClick={() => handleScan(device)}
                      disabled={!device.is_connected || scanning === device.id}
                      variant="ghost"
                      size="sm"
                      className="flex-1 btn-tactical btn-tactical-primary text-xs"
                      data-testid={`scan-hardware-${device.id}`}
                    >
                      <Scan className="w-3 h-3 mr-1" />
                      {scanning === device.id ? "Escaneando..." : "Escanear"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
          </div>
        </>
      )}

      {/* Scan Result with Spectrum Chart */}
      {scanResult && (
        <div className="mt-6 bg-[#111111] border border-[#34C759]/30 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-[#34C759]">
              Resultado del Escaneo - {scanResult.band_range}
            </h3>
            <span className="text-xs text-[#71717A]">
              {scanResult.points_count} puntos capturados
            </span>
          </div>
          
          {/* Spectrum Chart */}
          <div className="h-72 w-full mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={scanResult.scan_data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="hardwareGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#34C759" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#34C759" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.05)"
                  vertical={false}
                />
                <XAxis
                  dataKey="frequency"
                  tick={{
                    fill: "#71717A",
                    fontSize: 10,
                    fontFamily: "JetBrains Mono",
                  }}
                  tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  tickFormatter={(v) => `${v.toFixed(0)}`}
                  interval={Math.floor(scanResult.scan_data.length / 15)}
                />
                <YAxis
                  tick={{
                    fill: "#71717A",
                    fontSize: 10,
                    fontFamily: "JetBrains Mono",
                  }}
                  tickLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  domain={[-100, -20]}
                  tickFormatter={(v) => `${v}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  y={noiseFloorLevel}
                  stroke="#FF3B30"
                  strokeDasharray="5 5"
                  strokeOpacity={0.5}
                  label={{
                    value: "Noise Floor",
                    fill: "#FF3B30",
                    fontSize: 10,
                    position: "right",
                  }}
                />
                <ReferenceLine
                  y={-50}
                  stroke="#FFCC00"
                  strokeDasharray="5 5"
                  strokeOpacity={0.3}
                  label={{
                    value: "Strong Signal",
                    fill: "#FFCC00",
                    fontSize: 10,
                    position: "right",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="level"
                  stroke="#34C759"
                  fill="url(#hardwareGradient)"
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Legend and Noise Floor Control */}
          <div className="flex items-center justify-between pt-4 border-t border-white/[0.08]">
            <div className="flex items-center gap-6 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#34C759]" />
                <span className="text-[#71717A]">Señal RF</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-[#FF3B30]" style={{ borderStyle: "dashed" }} />
                <span className="text-[#71717A]">Piso de Ruido ({noiseFloorLevel} dBm)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-[#FFCC00]" style={{ borderStyle: "dashed" }} />
                <span className="text-[#71717A]">Señal Fuerte (-50 dBm)</span>
              </div>
            </div>
            
            {/* Noise Floor Slider Control */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#71717A]">Piso de Ruido:</span>
              <input
                type="range"
                min="-120"
                max="-50"
                value={noiseFloorLevel}
                onChange={(e) => setNoiseFloorLevel(parseInt(e.target.value))}
                className="w-24 h-1.5 bg-[#2A2A2A] rounded-lg appearance-none cursor-pointer accent-[#FF3B30]"
                data-testid="noise-floor-slider"
              />
              <span className="text-xs text-[#FF3B30] font-mono w-14">{noiseFloorLevel} dBm</span>
            </div>
          </div>
        </div>
      )}
        </TabsContent>

        {/* RF Network Control Dashboard - Only if feature is available */}
        {isFeatureAvailable('rf_network_control') && (
          <TabsContent value="rf-network">
            <RFNetworkDashboard />
          </TabsContent>
        )}
      </Tabs>

      {/* Add Device Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5]">
              Agregar Dispositivo RF
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <Label className="form-label">Tipo de Dispositivo</Label>
              <Select
                value={form.device_type}
                onValueChange={(v) => setForm({ ...form, device_type: v })}
              >
                <SelectTrigger className="input-tactical" data-testid="device-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="rf_analyzer">Analizador RF</SelectItem>
                  <SelectItem value="antenna_distributor">Distribuidor de Antena</SelectItem>
                  <SelectItem value="custom">Otro Dispositivo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label className="form-label">Nombre del Dispositivo</Label>
              <Input
                value={form.device_name}
                onChange={(e) => setForm({ ...form, device_name: e.target.value })}
                className="input-tactical"
                placeholder="Ej: RF Explorer Pro"
                required
                data-testid="device-name-input"
              />
            </div>

            <div className="form-group">
              <Label className="form-label">Tipo de Conexión</Label>
              <Select
                value={form.connection_type}
                onValueChange={(v) => setForm({ ...form, connection_type: v })}
              >
                <SelectTrigger className="input-tactical" data-testid="connection-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="network">Red (IP)</SelectItem>
                  <SelectItem value="serial">Puerto Serie (COM)</SelectItem>
                  <SelectItem value="usb">USB</SelectItem>
                  <SelectItem value="manual">Entrada Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.connection_type === "network" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label className="form-label">Dirección IP</Label>
                  <Input
                    value={form.ip_address}
                    onChange={(e) => setForm({ ...form, ip_address: e.target.value })}
                    className="input-tactical font-mono"
                    placeholder="192.168.1.100"
                    data-testid="ip-address-input"
                  />
                </div>
                <div className="form-group">
                  <Label className="form-label">Puerto</Label>
                  <Input
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: e.target.value })}
                    className="input-tactical font-mono"
                    placeholder="5000"
                    data-testid="port-input"
                  />
                </div>
              </div>
            )}

            {form.connection_type === "serial" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label className="form-label">Puerto Serie</Label>
                  <Input
                    value={form.serial_port}
                    onChange={(e) => setForm({ ...form, serial_port: e.target.value })}
                    className="input-tactical font-mono"
                    placeholder="COM3 o /dev/ttyUSB0"
                    data-testid="serial-port-input"
                  />
                </div>
                <div className="form-group">
                  <Label className="form-label">Baud Rate</Label>
                  <Select
                    value={form.baud_rate}
                    onValueChange={(v) => setForm({ ...form, baud_rate: v })}
                  >
                    <SelectTrigger className="input-tactical">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="9600">9600</SelectItem>
                      <SelectItem value="19200">19200</SelectItem>
                      <SelectItem value="38400">38400</SelectItem>
                      <SelectItem value="115200">115200</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {form.connection_type === "usb" && (
              <div className="space-y-4">
                <div className="p-4 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm">
                  <p className="text-sm text-[#34C759] font-medium mb-2">
                    <Upload className="w-4 h-4 inline mr-2" />
                    Flujo de trabajo USB
                  </p>
                  <ol className="text-xs text-[#A1A1AA] space-y-2 list-decimal list-inside">
                    <li>Conecta tu analizador RF (RF Explorer, etc.) al PC</li>
                    <li>Realiza el escaneo con el software del fabricante</li>
                    <li>Exporta los datos a CSV o TXT</li>
                    <li>Usa "Importar Escaneo" para cargar los datos aquí</li>
                  </ol>
                </div>
                <div className="form-group">
                  <Label className="form-label">Puerto USB (Referencia)</Label>
                  <Input
                    value={form.serial_port}
                    onChange={(e) => setForm({ ...form, serial_port: e.target.value })}
                    className="input-tactical font-mono"
                    placeholder="COM3 o /dev/ttyUSB0"
                  />
                  <p className="text-xs text-[#71717A] mt-1">Solo para referencia - el escaneo se importa manualmente</p>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDialog(false)}
                className="btn-tactical btn-tactical-secondary"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="btn-tactical btn-tactical-primary"
                data-testid="save-hardware-button"
              >
                Registrar Dispositivo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Scan Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#F4F4F5]">
              Importar Escaneo desde Analizador USB
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="file" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#0A0A0A]">
              <TabsTrigger value="file" className="data-[state=active]:bg-[#34C759] data-[state=active]:text-black">
                Archivo CSV/TXT
              </TabsTrigger>
              <TabsTrigger value="paste" className="data-[state=active]:bg-[#34C759] data-[state=active]:text-black">
                Pegar Datos
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="file" className="space-y-4 mt-4">
              <div className="form-group">
                <Label className="form-label">Seleccionar Archivo</Label>
                <div className="border-2 border-dashed border-white/20 rounded-sm p-8 text-center hover:border-[#34C759]/50 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.txt,.rfe"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="scan-file-input"
                  />
                  <label htmlFor="scan-file-input" className="cursor-pointer">
                    <FileUp className="w-12 h-12 mx-auto mb-4 text-[#71717A]" />
                    <p className="text-[#F4F4F5] mb-2">Click para seleccionar archivo</p>
                    <p className="text-xs text-[#71717A]">
                      Formatos: CSV, TXT, RFE (RF Explorer)
                    </p>
                  </label>
                </div>
                {importData && (
                  <p className="text-sm text-[#34C759] mt-2">
                    ✓ Archivo cargado: {importName}
                  </p>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="paste" className="space-y-4 mt-4">
              <div className="form-group">
                <Label className="form-label">Pegar datos del escaneo</Label>
                <Textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  className="input-tactical font-mono h-48 text-xs"
                  placeholder="Formato aceptado:&#10;480.000,-85.5&#10;480.500,-82.3&#10;481.000,-78.1&#10;...&#10;&#10;O formato RF Explorer:&#10;480.000 MHz: -85.5 dBm"
                  data-testid="import-data-textarea"
                />
              </div>
            </TabsContent>
          </Tabs>

          <div className="form-group">
            <Label className="form-label">Nombre del Escaneo</Label>
            <Input
              value={importName}
              onChange={(e) => setImportName(e.target.value)}
              className="input-tactical"
              placeholder="Ej: Escaneo Pre-Show Teatro Municipal"
              data-testid="import-name-input"
            />
          </div>

          <div className="p-4 bg-[#0A0A0A] border border-white/[0.08] rounded-sm">
            <h4 className="font-medium text-[#F4F4F5] mb-2">Formatos Soportados</h4>
            <ul className="text-xs text-[#71717A] space-y-1">
              <li>• <span className="text-[#34C759]">CSV estándar:</span> 520.000,-85.5</li>
              <li>• <span className="text-[#34C759]">CSV europeo:</span> 520,000;-85,5 (punto y coma)</li>
              <li>• <span className="text-[#34C759]">kHz sin decimal:</span> 520000,-85 (se convierte a MHz)</li>
              <li>• <span className="text-[#34C759]">RF Explorer:</span> 520.000 MHz: -85.5 dBm</li>
              <li>• <span className="text-[#34C759]">tinySA:</span> 520.000000,-85.50</li>
              <li>• <span className="text-[#FFCC00]">Wireless Workbench:</span> Exportación de frecuencias</li>
            </ul>
            <p className="text-[10px] text-[#71717A] mt-2 border-t border-white/[0.05] pt-2">
              Rango aceptado: 200-1200 MHz | Se ignoran encabezados automáticamente
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowImportDialog(false);
                setImportData("");
                setImportName("");
              }}
              className="btn-tactical btn-tactical-secondary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImportScan}
              disabled={!importData.trim()}
              className="btn-tactical btn-tactical-primary"
              data-testid="process-import-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar y Mostrar Espectro
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Scan Dialog */}
      <Dialog open={showSaveScanDialog} onOpenChange={setShowSaveScanDialog}>
        <DialogContent className="bg-[#111111] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
              <Save className="w-5 h-5 text-[#34C759]" />
              Guardar Escaneo
            </DialogTitle>
            <DialogDescription className="text-[#71717A]">
              Guarda el escaneo actual para usarlo después en la coordinación de frecuencias.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-[#A1A1AA]">Nombre del Escaneo</Label>
            <Input
              value={scanName}
              onChange={(e) => setScanName(e.target.value)}
              placeholder="Ej: Venue Principal - 2026-03-25"
              className="input-tactical mt-2"
              data-testid="scan-name-input"
            />
            
            {liveData.length > 0 && (
              <div className="mt-4 p-3 bg-[#0A0A0A] rounded-sm border border-white/[0.08]">
                <p className="text-xs text-[#71717A]">Información del escaneo:</p>
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-[#A1A1AA]">
                    <span className="text-[#71717A]">Puntos:</span> {liveData.filter(d => d.level > -100).length}
                  </p>
                  <p className="text-sm text-[#A1A1AA]">
                    <span className="text-[#71717A]">Rango:</span> {Math.min(...liveData.map(d => d.frequency)).toFixed(0)} - {Math.max(...liveData.map(d => d.frequency)).toFixed(0)} MHz
                  </p>
                  <p className="text-sm text-[#A1A1AA]">
                    <span className="text-[#71717A]">Dispositivo:</span> {detectedDevice}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={exportScanToCSV}
              className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 sm:mr-auto"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowSaveScanDialog(false)}
                className="text-[#71717A]"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => saveLiveScan()}
                disabled={savingScan || !scanName.trim()}
                className="bg-[#34C759] hover:bg-[#34C759]/90 text-black"
              >
                {savingScan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Escaneo
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        currentPlan={subscriptionUsage?.plan_name || "Gratuito"}
      />

      {/* Responsive Systems View - Floating on iPad, Sidebar on Desktop */}
      <ResponsiveSystemsView
        systems={systems}
        spectrumData={liveData}
        isVisible={showSystemsView}
        onToggleVisibility={() => setShowSystemsView(false)}
        isScanning={liveScanning}
      />
    </Layout>
  );
}
